/**
 * The runtime diagnostics table, its badges, and its loading state.
 *
 * @module
 */
import {
  buildPrimaryFetchInit,
  resolvePrimaryEnvironmentHttpUrl,
} from "../../environments/primary";
import { ForgeRuntimeViewOverride } from "../../environments/runtimeSurface";
import { cn } from "../../lib/utils";
import { Skeleton } from "../ui/skeleton";
import { formatCpuPercent, formatResidentMemory } from "./DiagnosticsProcessTable.logic";
import {
  filterRuntimeInventoryServices,
  summarizeRuntimeTools,
} from "./DiagnosticsSettingsPanel.logic";
import { RuntimeDiagnosticsResponse } from "@cmmd-forge/contracts";

const RUNTIME_DIAGNOSTICS_SKELETON_KEYS = ["runtime", "tools", "process", "fetched"] as const;

export async function fetchCurrentRuntimeDiagnostics(): Promise<RuntimeDiagnosticsResponse> {
  const endpointUrl = resolvePrimaryEnvironmentHttpUrl("/api/admin/runtime/diagnostics");
  const response = await fetch(endpointUrl, buildPrimaryFetchInit({ credentials: "include" }));
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { readonly error?: string };
    throw new Error(body.error ?? `Failed to load runtime diagnostics (${response.status})`);
  }
  return (await response.json()) as RuntimeDiagnosticsResponse;
}

export const RUNTIME_VIEW_OVERRIDE_LABELS: Record<ForgeRuntimeViewOverride, string> = {
  auto: "Auto",
  "cloud-official": "Cloud official",
  "desktop-local": "Desktop local",
  "local-web-dev": "Local web dev",
};

export function formatDiagnosticsTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function DiagnosticMetric({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="rounded-md border border-border/80 bg-muted/20 px-3 py-2">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
        {label}
      </div>
      <div className="mt-1 truncate font-mono text-xs text-foreground">{value}</div>
    </div>
  );
}

function RuntimeDiagnosticStatusBadge({ status }: { readonly status: "ok" | "error" }) {
  return (
    <span
      className={cn(
        "inline-flex rounded-sm border px-2 py-1 text-xs font-medium",
        status === "ok"
          ? "border-success-border bg-success-surface text-success-foreground"
          : "border-destructive-border bg-destructive-surface text-destructive",
      )}
    >
      {status}
    </span>
  );
}

function RuntimeServiceStatusBadge({ status }: { readonly status: "configured" | "missing" }) {
  return (
    <span
      className={cn(
        "inline-flex rounded-sm border px-2 py-1 text-xs font-medium",
        status === "configured"
          ? "border-success-border bg-success-surface text-success-foreground"
          : "border-border bg-muted text-muted-foreground",
      )}
    >
      {status}
    </span>
  );
}

function formatRuntimeServiceEndpoint(service: {
  readonly port?: number | string;
  readonly status: "configured" | "missing";
  readonly url?: string;
}): string {
  if (service.url) return service.url;
  if (service.port) return String(service.port);
  return service.status === "configured" ? "internal" : "not configured";
}

export function RuntimeDiagnosticsSkeleton() {
  return (
    <div className="space-y-4 px-4 py-4">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {RUNTIME_DIAGNOSTICS_SKELETON_KEYS.map((key) => (
          <Skeleton key={key} className="h-14 rounded-md" />
        ))}
      </div>
      <Skeleton className="h-40 rounded-md" />
      <Skeleton className="h-40 rounded-md" />
    </div>
  );
}

export function RuntimeDiagnosticsDataView({
  diagnostics,
}: {
  readonly diagnostics: RuntimeDiagnosticsResponse;
}) {
  const currentProcess = diagnostics.currentProcess;
  const services = filterRuntimeInventoryServices(diagnostics.localServices ?? [], diagnostics);

  return (
    <div className="space-y-4 px-4 py-4">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <DiagnosticMetric
          label="Runtime"
          value={
            diagnostics.runtime.sandboxRuntimeVersion ??
            diagnostics.runtime.commit ??
            diagnostics.runtime.nodeEnv ??
            "unknown"
          }
        />
        <DiagnosticMetric label="Tools" value={summarizeRuntimeTools(diagnostics.tools)} />
        <DiagnosticMetric
          label="Process"
          value={currentProcess ? `pid ${currentProcess.pid}` : "unknown"}
        />
        <DiagnosticMetric
          label="Fetched"
          value={formatDiagnosticsTimestamp(diagnostics.fetchedAt)}
        />
      </div>

      {currentProcess ? (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <DiagnosticMetric label="CWD" value={currentProcess.cwd ?? "unknown"} />
          <DiagnosticMetric
            label="Platform"
            value={`${currentProcess.platform}/${currentProcess.arch}`}
          />
          <DiagnosticMetric label="Node" value={currentProcess.nodeVersion} />
          <DiagnosticMetric label="Uptime" value={`${currentProcess.uptimeSeconds}s`} />
          <DiagnosticMetric
            label="Dev instance"
            value={currentProcess.forgeDevInstance ?? "unset"}
          />
          <DiagnosticMetric label="Forge port" value={currentProcess.forgePort ?? "unset"} />
        </div>
      ) : null}

      <div className="overflow-hidden rounded-md border border-border">
        <div className="border-b border-border bg-muted/45 px-3 py-2 text-xs font-medium text-muted-foreground">
          Runtime services
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-muted/25 text-left text-xs font-medium text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Service</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Endpoint</th>
                <th className="px-3 py-2">Detail</th>
              </tr>
            </thead>
            <tbody>
              {services.map((service) => (
                <tr key={service.name} className="border-t border-border/70">
                  <td className="px-3 py-2 font-medium">{service.name}</td>
                  <td className="px-3 py-2">
                    <RuntimeServiceStatusBadge status={service.status} />
                  </td>
                  <td className="max-w-[420px] px-3 py-2">
                    <code className="block truncate text-xs">
                      {formatRuntimeServiceEndpoint(service)}
                    </code>
                  </td>
                  <td className="max-w-[320px] px-3 py-2 text-xs text-muted-foreground">
                    <span className="block truncate">{service.detail ?? ""}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,380px)]">
        <div className="overflow-hidden rounded-md border border-border">
          <div className="border-b border-border bg-muted/45 px-3 py-2 text-xs font-medium text-muted-foreground">
            Tools
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-muted/25 text-left text-xs font-medium text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Tool</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Output</th>
                </tr>
              </thead>
              <tbody>
                {diagnostics.tools.map((tool) => (
                  <tr key={tool.name} className="border-t border-border/70">
                    <td className="px-3 py-2 font-medium">{tool.name}</td>
                    <td className="px-3 py-2">
                      <RuntimeDiagnosticStatusBadge status={tool.status} />
                    </td>
                    <td className="max-w-[520px] px-3 py-2">
                      <code className="block truncate text-xs">
                        {tool.output ?? tool.error ?? "No output"}
                      </code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="overflow-hidden rounded-md border border-border">
          <div className="border-b border-border bg-muted/45 px-3 py-2 text-xs font-medium text-muted-foreground">
            Processes
          </div>
          <div className="max-h-72 overflow-auto">
            <table className="w-full border-collapse text-xs">
              <thead className="bg-muted/25 text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">PID</th>
                  <th className="px-3 py-2">STAT</th>
                  {/* `ps`'s %cpu is a lifetime average on Linux, so the column
                      is named for what it is rather than implying a live read. */}
                  <th className="px-3 py-2 text-right">CPU avg</th>
                  <th className="px-3 py-2 text-right">Memory</th>
                  <th className="px-3 py-2">Command</th>
                </tr>
              </thead>
              <tbody>
                {diagnostics.processes.map((process) => (
                  <tr
                    key={`${process.pid}-${process.command}`}
                    className="border-t border-border/70"
                  >
                    <td className="px-3 py-2 font-mono">{process.pid}</td>
                    <td className="px-3 py-2 font-mono">{process.stat}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      {formatCpuPercent(process.cpuPercent)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      {formatResidentMemory(process.rssKb)}
                    </td>
                    <td className="px-3 py-2 font-mono">{process.command}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
